import React, { useState, ReactElement, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { ComboBoxFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import { SiteReadDto } from "../../../../Models/Site/SiteReadDto";
import { SiteCreateDto } from "../../../../Models/Site/SiteCreateDto";
import { timezonesToComboboxItems, timezoneToComboboxItem } from "../../../Area/Utilities/AreaUtilities";
import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormComboBox } from "../../../Shared/Components/Forms/FormsComponents";
import { nameValidator, descriptionValidator, timezoneValidator } from "../../Utilities/SiteValidationSchema";

interface Props {
  site: SiteReadDto | null;
  existingSites?: SiteReadDto[];
  onClose: () => void;
  onSave: (siteId: string | null, siteData: SiteCreateDto) => void;
}

export default function SiteDialog(props: Props): ReactElement {
  const localizationService = useLocalization();
  
  // Initial features state - memoized to prevent unnecessary re-renders
  const initialFeatures = useMemo(() => ({
    isManualSystemEnabled: props.site?.softwareFeatures?.isManualSystemEnabled || false,
    isAutomaticSystemEnabled: props.site?.softwareFeatures?.isAutomaticSystemEnabled || false,
    isFlowRateSystemEnabled: props.site?.softwareFeatures?.isFlowRateSystemEnabled || false,
    isAssetDashboardEnabled: props.site?.softwareFeatures?.isAssetDashboardEnabled || false,
    isHealthDashboardEnabled: props.site?.softwareFeatures?.isHealthDashboardEnabled || false,
  }), [props.site]);
  
  const [features, setFeatures] = useState(initialFeatures);
  
  // Sync features when site changes
  useEffect(() => {
    setFeatures(initialFeatures);
  }, [initialFeatures]);
  
  const [timezoneData, setTimezoneData] = useState(timezonesToComboboxItems().slice());

  // Check size of the window and set the dialog width
  const [dialogWidth, setDialogWidth] = useState<string>("500px");
  useEffect(() => {
    const checkSize = () => {
      if (window.innerWidth < 768) {
        setDialogWidth("95vw");
      } else {
        setDialogWidth("500px");
      }
    };

    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  const filterData = (filter: FilterDescriptor) => {
    const data = timezonesToComboboxItems().slice();
    return filterBy(data, filter);
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    setTimezoneData(filterData(event.filter));
  };

  // Validators from validation schema
  const nameValidatorFn = useMemo(
    () => nameValidator({
      existingSites: props.existingSites,
      currentSite: props.site,
      localizationService
    }),
    [props.existingSites, props.site, localizationService]
  );

  const descriptionValidatorFn = useMemo(
    () => descriptionValidator({ localizationService }),
    [localizationService]
  );

  const timezoneValidatorFn = useMemo(
    () => timezoneValidator({ localizationService }),
    [localizationService]
  );

  // Guard against duplicate saves from rapid double-clicks (onSave fires and the
  // dialog closes without awaiting, so a plain disabled flag can't block in time)
  const hasSubmittedRef = useRef(false);

  const handleSubmit = (dataItem: any) => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    const siteData: SiteCreateDto = {
      name: dataItem.name || "",
      description: dataItem.description || "",
      timezone: dataItem.timezone?.value || dataItem.timezone || "Australia/Brisbane",
      softwareFeatures: features,
    };
    props.onSave(props.site?.id || null, siteData);
  };

  // Manual submit handler that bypasses form validation issues
  const handleManualSubmit = (formRenderProps: any) => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    const formValues = {
      name: formRenderProps.valueGetter('name') || '',
      description: formRenderProps.valueGetter('description') || '',
      timezone: formRenderProps.valueGetter('timezone'),
    };
    
    const siteData: SiteCreateDto = {
      name: formValues.name,
      description: formValues.description,
      timezone: formValues.timezone?.value || formValues.timezone || "Australia/Brisbane",
      softwareFeatures: features,
    };
    
    props.onSave(props.site?.id || null, siteData);
  };

  const initialValues = {
    name: props.site?.name || "",
    description: props.site?.description || "",
    timezone: timezoneToComboboxItem(props.site?.timezone || "Australia/Brisbane"),
  };

  // Helper function to check if features have changed
  const hasFeatureChanges = useMemo(() => {
    if (!props.site) return true; // Always allow changes when creating new
    return (
      features.isManualSystemEnabled !== initialFeatures.isManualSystemEnabled ||
      features.isAutomaticSystemEnabled !== initialFeatures.isAutomaticSystemEnabled ||
      features.isFlowRateSystemEnabled !== initialFeatures.isFlowRateSystemEnabled ||
      features.isAssetDashboardEnabled !== initialFeatures.isAssetDashboardEnabled ||
      features.isHealthDashboardEnabled !== initialFeatures.isHealthDashboardEnabled
    );
  }, [features, props.site, initialFeatures]);

  // Helper function to check if form values have changed
  const formValuesChanged = (formValues: any) => {
    if (!props.site) return true; // Always allow changes when creating new
    
    const initialTimezoneValue = initialValues.timezone?.value || props.site?.timezone || "Australia/Brisbane";
    const currentTimezoneValue = formValues.timezone?.value || formValues.timezone || initialTimezoneValue;
    
    return (
      (formValues.name || "").trim() !== (props.site?.name || "").trim() ||
      (formValues.description || "").trim() !== (props.site?.description || "").trim() ||
      currentTimezoneValue !== initialTimezoneValue
    );
  };

  return (
    <Dialog
      title={props.site ? "Edit Account" : "Add Account"}
      onClose={props.onClose}
      width={dialogWidth}
    >
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps) => {
          // Compute disabled state outside the button prop for better reactivity
          const nameValue: string = formRenderProps.valueGetter('name') || '';
          const descriptionValue: string = formRenderProps.valueGetter('description') || '';
          const timezoneValue: any = formRenderProps.valueGetter('timezone');
          const nameError = nameValidatorFn(nameValue, formRenderProps.valueGetter, { name: 'name' });
          const descriptionError = descriptionValidatorFn(descriptionValue, formRenderProps.valueGetter, { name: 'description' });
          const timezoneError = timezoneValidatorFn(timezoneValue, formRenderProps.valueGetter, { name: 'timezone' });
          
          // Check if validation passes
          const validationPasses = (nameError === '' && descriptionError === '' && timezoneError === '');
          
          // Compute if save should be disabled
          let isSaveDisabled = false;
          if (!validationPasses) {
            isSaveDisabled = true; // Disable if validation fails
          } else if (props.site) {
            // If editing, check if there are any changes
            const currentFormValues = {
              name: nameValue,
              description: descriptionValue,
              timezone: timezoneValue
            };
            const hasFormChanges = formValuesChanged(currentFormValues);
            // hasFeatureChanges is computed via useMemo and will update when features change
            isSaveDisabled = !(hasFormChanges || hasFeatureChanges); // Disable if no changes
          }
          // For new sites, allow save if validation passes
          
          return (
          <FormElement style={{ padding: '16px' }}>
            <div className="mb-4">
              <Field
                name="name"
                component={FormInput}
                label={localizationService.toLanguageString("custom.name", enMessages.custom.name)}
                validator={nameValidatorFn}
                validatorRequired={true}
                style={{ width: "100%" }}
              />
            </div>

            <div className="mb-4">
              <Field
                name="description"
                component={FormInput}
                label={localizationService.toLanguageString("custom.description", enMessages.custom.description)}
                validator={descriptionValidatorFn}
                validatorRequired={true}
                style={{ width: "100%" }}
              />
            </div>

            <div className="mb-4">
              <Field
                name="timezone"
                component={FormComboBox}
                label={localizationService.toLanguageString("custom.timezone", enMessages.custom.timezone)}
                validator={timezoneValidatorFn}
                validatorRequired={true}
                data={timezoneData}
                textField="displayName"
                placeholder={localizationService.toLanguageString("custom.timezone", enMessages.custom.timezone)}
                filterable={true}
                onFilterChange={filterChange}
                style={{ width: "100%" }}
              />
            </div>

            <div className="mb-4">
              <label className="k-label k-margin-bottom-sm">
                Features
              </label>
              <div className="k-padding-sm p-3" style={{ border: '1px solid #eaeaea', borderRadius: '4px' }}>
                <div className="mb-3 flex items-center gap-2">
                  <div className="w-44">Manual System</div>
                  <Switch
                    checked={features.isManualSystemEnabled}
                    onChange={(e) => setFeatures({ ...features, isManualSystemEnabled: e.value })}
                    size={"small"}
                  />
                </div>
                <div className="mb-3 flex items-center gap-2">
                  <div className="w-44">Automatic System</div>
                  <Switch
                    checked={features.isAutomaticSystemEnabled}
                    onChange={(e) => setFeatures({ ...features, isAutomaticSystemEnabled: e.value })}
                    size={"small"}
                  />
                </div>
                <div className="mb-3 flex items-center gap-2">
                  <div className="w-44">Flow Rate System</div>
                  <Switch
                    checked={features.isFlowRateSystemEnabled}
                    size={"small"}
                    onChange={(e) => setFeatures({ ...features, isFlowRateSystemEnabled: e.value })}
                  />
                </div>
                <div className="mb-3 flex items-center gap-2">
                  <div className="w-44">Asset Dashboard</div>
                  <Switch
                    checked={features.isAssetDashboardEnabled}
                    onChange={(e) => setFeatures({ ...features, isAssetDashboardEnabled: e.value })}
                    size={"small"}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-44">Health Dashboard</div>
                  <Switch
                    checked={features.isHealthDashboardEnabled}
                    onChange={(e) => setFeatures({ ...features, isHealthDashboardEnabled: e.value })}
                    size={"small"}
                  />
                </div>
              </div>
            </div>

            <DialogActionsBar>
              <Button themeColor="base" onClick={props.onClose}>
                {localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
              </Button>
              <Button 
                type="button"
                themeColor="primary" 
                disabled={isSaveDisabled}
                onClick={() => {
                  if (!isSaveDisabled) {
                    handleManualSubmit(formRenderProps);
                  }
                }}
              >
                {localizationService.toLanguageString("custom.save", enMessages.custom.save)}
              </Button>
            </DialogActionsBar>
          </FormElement>
          );
        }}
      />
    </Dialog>
  );
}

